import { isSupportedSortKey } from '@src/utils/presets/sortKeys';
import { cloneDeep } from 'lodash';

export const APPLY_PRESET_VIEW = 'trends/APPLY_PRESET_VIEW';
export const PERSONAL_PRESETS_KEY = '/personal-presets';
export const PERSONAL_PRESETS_FEATURE = 'enableVideoPersonalPresets';

export type PresetSource = {
  id: string;
  name: string;
  kind: 'conviva' | 'dashboard';
  isPublic?: boolean;
  config?: Record<string, any>;
};

export interface PresetViewConfig {
  metricsChartContainer: { metric: string; metrics: string[] };
  base: { dimensions: string[]; dimensionMetrics: string[]; globalOrderBy: string };
  metricPanel: Record<string, any>;
  dimensionPanel: Record<string, any>;
  sections: { summary: boolean; metric: boolean; dimension: boolean };
  summaryWidgets: Record<string, any>;
  widgetState: { widgets: Record<string, any>; order: string[] };
}

// The operation never writes these fields; they belong to the receiving dashboard.
export interface AnalysisContext {
  common: Record<string, any>;
  comparison: Record<string, any>;
  base: Record<string, any>;
  dateCompare: boolean;
  tableCompare: boolean;
  sliceArea: unknown;
  zoomArea: unknown;
}

export const composePreset = (view: PresetViewConfig, context: AnalysisContext) => ({
  view: cloneDeep(view),
  context: cloneDeep(context),
});

export function normalizePreset(
  state: Record<string, any>,
  defaults: PresetViewConfig,
  supported: { metrics: string[]; dimensions: string[]; widgetTypes: string[] },
): PresetViewConfig {
  if (!state || !state.metricsChartContainer) throw new Error('This dashboard has no Trends view.');
  const base = state['module:trends']?.base || {};
  const metricPanel = state.metricPanel || {};
  const view: PresetViewConfig = cloneDeep({
    metricsChartContainer: {
      metric: state.metricsChartContainer.metric,
      metrics: state.metricsChartContainer.metrics,
    },
    base: {
      dimensions: base.dimensions ?? defaults.base.dimensions,
      dimensionMetrics: base.dimensionMetrics ?? defaults.base.dimensionMetrics,
      globalOrderBy: base.globalOrderBy ?? defaults.base.globalOrderBy,
    },
    metricPanel: Object.fromEntries(
      Object.entries(defaults.metricPanel).map(([key, value]) => [
        key,
        metricPanel[key] ?? (key === 'layout' && metricPanel.size ? `4${metricPanel.size}` : value),
      ]),
    ),
    dimensionPanel: {
      activeTab: state.dimensionPanel?.activeTab ?? defaults.dimensionPanel.activeTab,
      sorts: state.dimensionPanel?.sorts ?? {},
      height: state.dimensionPanel?.height ?? 's',
    },
    sections: { ...defaults.sections, ...state.toggles?.sections },
    summaryWidgets: { ...defaults.summaryWidgets, ...state.summaryWidgets },
    widgetState: state.widgetState
      ? { widgets: state.widgetState.widgets, order: state.widgetState.order ?? [] }
      : defaults.widgetState,
  });
  const validateList = (items: unknown, allowed: string[], label: string, max = Infinity) => {
    if (
      !Array.isArray(items) ||
      !items.length ||
      items.length > max ||
      new Set(items).size !== items.length ||
      items.some((item) => !allowed.includes(item))
    ) {
      throw new Error(
        `The preset contains unavailable or invalid ${label}. Update the saved dashboard and try again.`,
      );
    }
  };
  validateList(view.metricsChartContainer.metrics, supported.metrics, 'metrics');
  validateList(view.base.dimensions, supported.dimensions, 'dimensions', 8);
  validateList(view.base.dimensionMetrics, supported.metrics, 'dimension metrics', 20);
  if (new Set([...view.base.dimensionMetrics, view.base.globalOrderBy]).size > 20) {
    throw new Error(
      'The preset exceeds the 20-column dimension limit including its sorting metric.',
    );
  }
  if (
    !view.metricsChartContainer.metrics.includes(view.metricsChartContainer.metric) ||
    !supported.metrics.includes(view.base.globalOrderBy)
  ) {
    throw new Error('The preset has an unavailable focused or sorting metric.');
  }
  if (
    [
      ...Object.values(view.sections),
      ...[
        'diagnosticsMode',
        'yScaleMode',
        'logAxis',
        'primaryFilterVisibility',
        'widgetPanelVisibility',
      ].map((key) => view.metricPanel[key]),
    ].some((value) => typeof value !== 'boolean') ||
    ![0, 1].includes(view.metricPanel.activeTab) ||
    ![0, 1, 2].includes(view.dimensionPanel.activeTab) ||
    !['s', 'm', 'l'].includes(view.dimensionPanel.height)
  ) {
    throw new Error('The preset has an unsupported panel configuration.');
  }
  if (
    !['#', '%', '%T'].includes(view.metricPanel.unit) ||
    !['1s', '2s', '2l', '3s', '3l', '4s', '4m', '4l'].includes(view.metricPanel.layout)
  ) {
    throw new Error('The preset has an unsupported display format or layout.');
  }
  if (
    !view.widgetState?.widgets ||
    !Array.isArray(view.widgetState.order) ||
    Object.values(view.widgetState.widgets).some(
      (widget: any) => !supported.widgetTypes.includes(widget.type),
    )
  ) {
    throw new Error('The preset contains unavailable widgets.');
  }
  // Deleted widgets can remain in saved order arrays; mirror the widget renderer.
  view.widgetState.order = view.widgetState.order.filter((id) =>
    Boolean(view.widgetState.widgets[id]),
  );
  if (view.summaryWidgets.type && view.summaryWidgets.type !== 'SPI')
    throw new Error('The preset has an unsupported summary panel.');
  if (!Array.isArray(view.summaryWidgets.widgets))
    throw new Error('The preset has invalid summary widgets.');
  for (const widget of Object.values(view.widgetState.widgets) as any[]) {
    if (widget.state?.metrics?.length)
      validateList(widget.state.metrics, supported.metrics, 'widget metrics');
    for (const key of ['numerator', 'denominator']) {
      if (widget.state?.[key] && !supported.metrics.includes(widget.state[key]))
        throw new Error('The preset contains an unavailable widget metric.');
    }
  }
  for (const widget of view.summaryWidgets.widgets || []) {
    if (widget.config?.metrics?.length)
      validateList(widget.config.metrics, supported.metrics, 'summary metrics');
  }
  // Old sorts for removed dimensions are not part of the displayed view.
  view.dimensionPanel.sorts = Object.fromEntries(
    Object.entries(view.dimensionPanel.sorts).filter(([dimension]) =>
      view.base.dimensions.includes(dimension),
    ),
  );
  for (const [dimension, sort] of Object.entries(view.dimensionPanel.sorts) as [string, any][]) {
    if (
      !view.base.dimensions.includes(dimension) ||
      !['asc', 'desc'].includes(sort.order) ||
      !isSupportedSortKey(sort.key, dimension, supported.metrics)
    ) {
      throw new Error('The preset contains an unavailable table sort.');
    }
  }
  return view;
}

export function groupPresets(sources: PresetSource[], pinned: string[], search: string) {
  const groups: Record<string, PresetSource[]> = {
    'My presets': [],
    'Saved dashboards': [],
    'Conviva presets': [],
  };
  const seen = new Set<string>();
  for (const source of sources) {
    const key = `${source.kind}:${source.id}`;
    if (
      seen.has(key) ||
      !source.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
    )
      continue;
    seen.add(key);
    const group =
      source.kind === 'conviva'
        ? 'Conviva presets'
        : pinned.includes(source.id)
          ? 'My presets'
          : 'Saved dashboards';
    groups[group].push(source);
  }
  Object.values(groups).forEach((items) => items.sort((a, b) => a.name.localeCompare(b.name)));
  return groups;
}
